import React, { useState, useRef, useCallback, useLayoutEffect, useEffect } from 'react';
import './ChatView.css';
import { DropZoneOverlay } from '@/components/ui/DropZoneOverlay/DropZoneOverlay';
import { OverlayScrollArea } from '@/components/ui/OverlayScrollArea/OverlayScrollArea';
import { MessengerScrollDownFab } from '@/components/Messenger/shared/chrome/MessengerScrollDownFab';
import { MessengerMessagesLoader } from '@/components/Messenger/shared/chrome/MessengerMessagesLoader';
import {
  cancelMessengerTailScrollAnimation,
  clampViewportScrollTop,
  isMessengerTailStuckToVisualBottom,
  isMessengerViewportAtBottom,
  messengerTailAppendCount,
  scrollMessengerTailIntoView,
  scrollMessengerViewportInstant,
  viewportMaxScrollTop,
} from '@/components/Messenger/shared/viewport/messengerViewportScroll';
import { isDirectThreadTailJumpActive } from '@/components/Messenger/cache/directThreadCacheStore';
import {
  applyDmViewportResumePosition,
  type DmScrollAnchor,
} from '@/components/Messenger/shared/viewport/messengerDmScrollAnchor';
function resumePropToDmAnchor(
  a: { messageId: string; offsetFromViewportTop: number } | undefined
): DmScrollAnchor | null {
  if (!a) return null;
  return { anchorMessageId: a.messageId, offsetFromViewportTop: a.offsetFromViewportTop };
}

function isDocumentVisibilityHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState !== 'visible';
}

/** Изолирует ленту от внутреннего scroll-state ChatView (FAB, reveal, pending). */
const ChatViewMessageFeedSlot = React.memo(function ChatViewMessageFeedSlot({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
});

export interface ChatViewProps {
  header: React.ReactNode;
  composer: React.ReactNode;
  /** Дети ленты — порядок DOM сверху вниз: старые → новые. */
  children: React.ReactNode;

  /**
   * ID of the newest / tail message. When it changes and user was at bottom,
   * ChatView auto-scrolls. ChatView also increments pendingNewBelow when
   * the user is scrolled up.
   */
  tailMessageId?: string;

  /** ID самого старого сообщения в DOM-окне — для отличия loadOlder/prepend от append в хвост. */
  headMessageId?: string;

  /** Длина списка сообщений (для счётчика «новых ниже»: замена opt→server не считается дважды). */
  messageListLength: number;

  /**
   * Direct: false, пока первый fetch в полёте и сообщений ещё 0 — иначе маска скролла снимется
   * до появления ленты. У агента не передавать (по умолчанию true).
   */
  messageFeedReady?: boolean;

  /**
   * Context key (chatId / threadId). When it changes, scroll state resets —
   * При смене контекста позиция сбрасывается; первый кадр докручиваем к низу.
   */
  contextId?: string;

  /** Shows "К ответу" hint on the scroll-down FAB instead of "К низу". */
  streamCue?: boolean;

  /**
   * DnD: ChatView owns isDraggingOver state and renders the drop overlay.
   * Parent provides only drop business logic.
   */
  acceptDrop?: boolean;
  onDrop?: React.DragEventHandler<HTMLDivElement>;
  /** Called after ChatView's own preventDefault/setIsDraggingOver. */
  onDragOver?: React.DragEventHandler<HTMLDivElement>;

  /**
   * Exposed scroll viewport ref — for parent's special needs
   * (pagination scroll restore in DirectThreadView).
   */
  viewportRef?: React.MutableRefObject<HTMLElement | null>;

  /** Called when user reaches the visual bottom (for mark-as-read, etc.). */
  onScrolledToBottom?: () => void;

  /** Любой скролл ленты (для прочтения по видимости и т.п.). */
  onMessagesViewportChange?: () => void;

  /**
   * Прямой чат: восстановить scrollTop при первом показе contextId (кэш LRU).
   * Если не передано — как раньше, к хвосту.
   */
  resumeScrollTop?: number;

  /**
   * Прямой чат: якорь (id сообщения + смещение от верха viewport) — точнее, чем один scrollTop.
   * Вместе с resumeScrollTop (fallback при отсутствии узла в DOM) из getDirectThreadScrollResume.
   */
  resumeScrollAnchor?: {
    messageId: string;
    offsetFromViewportTop: number;
  };

  /**
   * Direct: то же число, что бейдж у чата в сайдбаре (unread_count − вычитание).
   * Если задан — внутренний pendingNewBelow по append не используется.
   */
  scrollDownBadgeOverride?: number;

  /**
   * Прямой чат: при росте хвоста (`append > 0`) докручивать вниз, даже если лента была не у низа,
   * если последнее сообщение от этого пользователя (отправка своего текста/медиа).
   */
  tailSenderId?: string | null;
  autoScrollOnOwnTailAppendUserId?: string | null;

  /** После первичной установки scroll (resume или к низу), чтобы родитель пересчитал разделитель/FAB. */
  onResumeScrollSettled?: () => void;

  /**
   * Поповер мессенджера: false, пока окно закрыто/анимация выхода — после true догоняем хвост, если были у низа
   * (размеры/scrollTop ломаются за пределами экрана). Вне попапа не передавать — по умолчанию true.
   */
  messengerSurfaceVisible?: boolean;
  /**
   * Внешний переход к конкретному сообщению (поиск/цитата): снимает первичные pin-флаги,
   * чтобы авто-докрутка к хвосту не перебила программный scroll.
   */
  externalScrollIntentSignal?: number;
  /** Скрыть ленту, пока внешний переход не выставил финальную позицию. */
  messagesRevealBlocked?: boolean;

  /**
   * DM: в кэше есть неподгруженные сообщения новее DOM-хвоста — не считать ленту «у низа».
   */
  hasRemoteNewerMessages?: boolean;

  /**
   * DM: перед скроллом FAB «к низу» — догрузить страницы до live-tail.
   */
  onBeforeJumpToLatest?: () => void | Promise<void>;
}

/**
 * Unified chat layout: колонка + DnD overlay + ScrollDownFab.
 * Both AgentChatView and DirectThreadView delegate their layout here.
 */
export function ChatView({
  header,
  composer,
  children,
  tailMessageId,
  headMessageId,
  messageListLength,
  messageFeedReady = true,
  contextId,
  streamCue = false,
  acceptDrop = false,
  onDrop,
  onDragOver,
  viewportRef: externalViewportRef,
  onScrolledToBottom,
  onMessagesViewportChange,
  resumeScrollTop,
  resumeScrollAnchor,
  scrollDownBadgeOverride,
  onResumeScrollSettled,
  messengerSurfaceVisible = true,
  tailSenderId,
  autoScrollOnOwnTailAppendUserId,
  externalScrollIntentSignal,
  messagesRevealBlocked = false,
  hasRemoteNewerMessages = false,
  onBeforeJumpToLatest,
}: ChatViewProps) {
  const internalViewportRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);
  const composerHostRef = useRef<HTMLDivElement | null>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  // Use external viewport ref if provided, otherwise internal
  const activeViewportRef = externalViewportRef ?? internalViewportRef;

  const prevAtBottomRef = useRef(true);
  /** Пока composer растёт: не сбрасывать «у низа» из-за кратковременного scrollTop≈0 (flex/OS). */
  const composerPinIntentRef = useRef(false);
  const composerPinUntilRef = useRef(0);
  /** Актуальный флаг — в handleScroll не трогаем prevAtBottom, пока попап скрыт (иначе ломается за экраном). */
  const messengerSurfaceVisibleRef = useRef(messengerSurfaceVisible);
  messengerSurfaceVisibleRef.current = messengerSurfaceVisible;
  const prevMessengerSurfaceVisibleRef = useRef<boolean | undefined>(undefined);
  const prevTailMessageIdRef = useRef<string | undefined>(undefined);
  const tailListSnapRef = useRef<{
    len: number;
    firstId: string | undefined;
    lastId: string | undefined;
  }>({
    len: 0,
    firstId: undefined,
    lastId: undefined,
  });

  const [liftedFromBottom, setLiftedFromBottom] = useState(false);
  const [pendingNewBelow, setPendingNewBelow] = useState(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  /** После смены contextId скрываем ленту до первичного scroll (иначе виден скачок сверху вниз). */
  const [scrollRevealedForContext, setScrollRevealedForContext] = useState<string | undefined>(
    undefined
  );

  /** Актуальный resumeScrollTop для ResizeObserver (без устаревшего замыкания). */
  const resumeScrollTopRef = useRef<number | undefined>(undefined);
  resumeScrollTopRef.current = resumeScrollTop;

  const resumeScrollAnchorRef = useRef<typeof resumeScrollAnchor>(undefined);
  resumeScrollAnchorRef.current = resumeScrollAnchor;

  /**
   * Первый кадр открытия: докрутка к низу / восстановление top — пока scrollHeight стабилизируется
   * (OverlayScrollbars, flex-end, картинки) или пока пользователь не прокрутил колесом.
   */
  const initialBottomPinActiveRef = useRef(false);
  const initialResumePinActiveRef = useRef(false);
  /** Пользователь явно прокрутил ленту — не переигрываем позицию из кэша и не «дожимаем» низ. */
  const userIntentScrollRef = useRef(false);
  /** Последний tailMessageId, обработанный ResizeObserver settle — чтобы не дублировать «к низу» с эффектом по хвосту. */
  const prevTailMessageIdAtResizeSettleRef = useRef<string | undefined>(undefined);

  /** Корень ленты внутри OS-content — нижний край учитывает все слоты (разделитель непрочитанных и т.д.). */
  const getScrollContentRoot = useCallback(() => contentRef.current?.firstElementChild ?? null, []);

  /** Хвост — последний ребёнок внутренней ленты (для scrollIntoView / якоря). */
  const getTailEl = useCallback(() => {
    const inner = contentRef.current?.firstElementChild;
    return inner?.lastElementChild ?? null;
  }, []);

  // ── Scroll tracking ────────────────────────────────────────────

  const handleScroll = useCallback(() => {
    const vp = activeViewportRef.current;
    if (!vp) return;
    if (!messengerSurfaceVisibleRef.current) {
      return;
    }
    const atCacheBottom = isMessengerTailStuckToVisualBottom(
      vp,
      getTailEl(),
      undefined,
      getScrollContentRoot()
    );
    const atBottom = atCacheBottom && !hasRemoteNewerMessages;
    const pinActive =
      composerPinIntentRef.current &&
      (typeof performance !== 'undefined' ? performance.now() : Date.now()) < composerPinUntilRef.current;
    if (pinActive && !atBottom) {
      scrollMessengerViewportInstant(vp);
      const recovered = isMessengerTailStuckToVisualBottom(
        vp,
        getTailEl(),
        undefined,
        getScrollContentRoot()
      );
      if (recovered) {
        prevAtBottomRef.current = true;
        setLiftedFromBottom(false);
        setPendingNewBelow(0);
        onScrolledToBottom?.();
      }
      onMessagesViewportChange?.();
      return;
    }
    prevAtBottomRef.current = atBottom;
    setLiftedFromBottom(!atBottom);
    if (atBottom) {
      setPendingNewBelow(0);
      onScrolledToBottom?.();
    }
    onMessagesViewportChange?.();
  }, [
    activeViewportRef,
    getTailEl,
    getScrollContentRoot,
    hasRemoteNewerMessages,
    onScrolledToBottom,
    onMessagesViewportChange,
  ]);

  const handleScrollRef = useRef(handleScroll);
  handleScrollRef.current = handleScroll;

  // ── Reset scroll state when context changes (layout: до эффекта хвоста) ──

  useLayoutEffect(() => {
    prevAtBottomRef.current = true;
    prevTailMessageIdRef.current = undefined;
    tailListSnapRef.current = { len: 0, firstId: undefined, lastId: undefined };
    setPendingNewBelow(0);
    setLiftedFromBottom(false);
    userIntentScrollRef.current = false;
    initialBottomPinActiveRef.current = false;
    initialResumePinActiveRef.current = false;
    // Сброс «ленту уже показали» при каждом входе в чат — как при первом открытии:
    // иначе scrollRevealedForContext === contextId и маска не включается → виден скачок.
    setScrollRevealedForContext(undefined);
    prevTailMessageIdAtResizeSettleRef.current = undefined;
  }, [contextId]);

  // ── Auto-scroll when a new tail message arrives ────────────────

  useLayoutEffect(() => {
    if (tailMessageId !== undefined) return;
    if (
      contextId !== undefined &&
      messageFeedReady &&
      messageListLength === 0
    ) {
      setScrollRevealedForContext(contextId);
    }
  }, [contextId, messageFeedReady, messageListLength, tailMessageId]);

  useLayoutEffect(() => {
    if (tailMessageId === undefined) return;

    if (tailMessageId === prevTailMessageIdRef.current) {
      tailListSnapRef.current = {
        len: messageListLength,
        firstId: headMessageId,
        lastId: tailMessageId,
      };
      return;
    }

    const isFirstLoad = prevTailMessageIdRef.current === undefined;
    const prevSnap = tailListSnapRef.current;
    const nextSnap = {
      len: messageListLength,
      firstId: headMessageId,
      lastId: tailMessageId,
    };

    prevTailMessageIdRef.current = tailMessageId;
    tailListSnapRef.current = nextSnap;

    if (isFirstLoad) {
      const reveal = () => {
        if (contextId !== undefined) setScrollRevealedForContext(contextId);
        onResumeScrollSettled?.();
      };

      /** Повторный snap после paint: scrollHeight часто растёт после первого layout (OS, изображения). */
      const scheduleInitialScrollBackup = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const v = activeViewportRef.current;
            if (!v) return;
            applyDmViewportResumePosition(v, {
              anchor: resumePropToDmAnchor(resumeScrollAnchorRef.current),
              fallbackScrollTop: resumeScrollTopRef.current,
              tailEl: getTailEl(),
            });
            handleScrollRef.current();
          });
        });
      };

      const runInitialScrollInLayout = (vp: HTMLElement) => {
        const applyPinsAfterScroll = () => {
          const rs = resumeScrollTopRef.current;
          const ra = resumeScrollAnchorRef.current;
          initialBottomPinActiveRef.current = rs === undefined && !ra;
          initialResumePinActiveRef.current = rs !== undefined || !!ra;
        };

        for (let i = 0; i < 48; i++) {
          const max = Math.max(0, vp.scrollHeight - vp.clientHeight);
          if (max > 0) {
            applyDmViewportResumePosition(vp, {
              anchor: resumePropToDmAnchor(resumeScrollAnchorRef.current),
              fallbackScrollTop: resumeScrollTopRef.current,
              tailEl: getTailEl(),
            });
            const atBottom = isMessengerTailStuckToVisualBottom(
              vp,
              getTailEl(),
              undefined,
              getScrollContentRoot()
            );
            prevAtBottomRef.current = atBottom;
            setLiftedFromBottom(!atBottom);
            applyPinsAfterScroll();
            reveal();
            scheduleInitialScrollBackup();
            return;
          }
          void contentRef.current?.offsetHeight;
          void vp.getBoundingClientRect();
        }
        applyDmViewportResumePosition(vp, {
          anchor: resumePropToDmAnchor(resumeScrollAnchorRef.current),
          fallbackScrollTop: resumeScrollTopRef.current,
          tailEl: getTailEl(),
        });
        const atBottom = isMessengerTailStuckToVisualBottom(
          vp,
          getTailEl(),
          undefined,
          getScrollContentRoot()
        );
        prevAtBottomRef.current = atBottom;
        setLiftedFromBottom(!atBottom);
        applyPinsAfterScroll();
        reveal();
        scheduleInitialScrollBackup();
      };

      const vp = activeViewportRef.current;
      if (!vp) {
        requestAnimationFrame(() => {
          const v = activeViewportRef.current;
          if (v) runInitialScrollInLayout(v);
          else {
            const rs = resumeScrollTopRef.current;
            const ra = resumeScrollAnchorRef.current;
            initialBottomPinActiveRef.current = rs === undefined && !ra;
            initialResumePinActiveRef.current = rs !== undefined || !!ra;
            reveal();
            scheduleInitialScrollBackup();
          }
        });
        return;
      }
      runInitialScrollInLayout(vp);
      return;
    }

    const append = messengerTailAppendCount(prevSnap, nextSnap);
    const ownTailAppend =
      append > 0 &&
      autoScrollOnOwnTailAppendUserId != null &&
      tailSenderId != null &&
      tailSenderId === autoScrollOnOwnTailAppendUserId;

    /** FAB догружает хвост пачками — один плавный скролл только после onBeforeJumpToLatest. */
    if (contextId && isDirectThreadTailJumpActive(contextId) && append > 0) {
      return;
    }

    if (!prevAtBottomRef.current) {
      if (append > 0 && scrollDownBadgeOverride === undefined && !ownTailAppend) {
        setPendingNewBelow((p) => p + append);
      }
      if (!ownTailAppend) {
        return;
      }
    }

    // В фоне rAF почти не бежит — плавный скролл не срабатывает; делаем сразу в layout.
    if (isDocumentVisibilityHidden()) {
      const vp = activeViewportRef.current;
      const tail = getTailEl();
      if (vp) {
        scrollMessengerTailIntoView(vp, tail, 'instant');
        const atBottom = isMessengerTailStuckToVisualBottom(
          vp,
          getTailEl(),
          undefined,
          getScrollContentRoot()
        );
        prevAtBottomRef.current = atBottom;
        setLiftedFromBottom(!atBottom);
        if (atBottom) setPendingNewBelow(0);
      }
      return;
    }

    // Schedule smooth scroll after paint (rAF × 2 pattern, same as DirectThreadView)
    let canceled = false;
    let rafInner = 0;
    const rafOuter = requestAnimationFrame(() => {
      if (canceled) return;
      rafInner = requestAnimationFrame(() => {
        rafInner = 0;
        if (canceled) return;
        const tail = getTailEl();
        scrollMessengerTailIntoView(activeViewportRef.current, tail, 'smooth');
      });
    });
    return () => {
      canceled = true;
      cancelAnimationFrame(rafOuter);
      if (rafInner) cancelAnimationFrame(rafInner);
    };
    // resumeScrollTop / resumeScrollAnchor — только через ref (актуальный кэш без лишних перезапусков при новой ссылке на объект из родителя).
  }, [
    contextId,
    tailMessageId,
    headMessageId,
    messageListLength,
    activeViewportRef,
    getTailEl,
    getScrollContentRoot,
    scrollDownBadgeOverride,
    onResumeScrollSettled,
    tailSenderId,
    autoScrollOnOwnTailAppendUserId,
  ]);

  // Пока попап скрыт — фиксируем ref (до paint), чтобы эффект открытия увидел false → true.
  useLayoutEffect(() => {
    if (!messengerSurfaceVisible) {
      prevMessengerSurfaceVisibleRef.current = false;
    }
  }, [messengerSurfaceVisible]);

  // Повторное открытие попапа: после rAF×2 позиционирования в ChatPopover — rAF×3 и догон по maxScrollTop (geometry врала).
  useEffect(() => {
    if (!messengerSurfaceVisible) return;

    const wasHidden = prevMessengerSurfaceVisibleRef.current === false;
    prevMessengerSurfaceVisibleRef.current = true;
    if (!wasHidden) return;

    let canceled = false;
    let rafOuter = 0;

    const settleToBottomIfFollowing = () => {
      if (canceled || !prevAtBottomRef.current) return;
      const vp = activeViewportRef.current;
      if (!vp) return;
      const maxTop = viewportMaxScrollTop(vp);
      const slack = maxTop - vp.scrollTop;
      if (slack <= 2) {
        setLiftedFromBottom(false);
        setPendingNewBelow(0);
        return;
      }
      const tail = getTailEl();
      scrollMessengerTailIntoView(vp, tail, 'instant');
      prevAtBottomRef.current = true;
      setLiftedFromBottom(false);
      setPendingNewBelow(0);
      onScrolledToBottom?.();
      requestAnimationFrame(() => {
        if (canceled) return;
        const v = activeViewportRef.current;
        if (!v || !prevAtBottomRef.current) return;
        if (viewportMaxScrollTop(v) - v.scrollTop > 2) {
          scrollMessengerViewportInstant(v);
        }
        if (viewportMaxScrollTop(v) - v.scrollTop <= 2) {
          onScrolledToBottom?.();
        }
      });
    };

    rafOuter = requestAnimationFrame(() => {
      if (canceled) return;
      requestAnimationFrame(() => {
        if (canceled) return;
        requestAnimationFrame(settleToBottomIfFollowing);
      });
    });

    return () => {
      canceled = true;
      cancelAnimationFrame(rafOuter);
    };
  }, [messengerSurfaceVisible, activeViewportRef, getTailEl, onScrolledToBottom]);

  // Догон хвоста при возврате фокуса: новые сообщения в фоне + сорванный rAF → prevAtBottom ещё true, визуально нет.
  useEffect(() => {
    const catchUpIfFollowingTail = () => {
      if (isDocumentVisibilityHidden()) return;
      const vp = activeViewportRef.current;
      if (!vp || !prevAtBottomRef.current) return;
      const tail = getTailEl();
      if (isMessengerTailStuckToVisualBottom(vp, tail, undefined, getScrollContentRoot())) return;
      scrollMessengerTailIntoView(vp, tail, 'instant');
      prevAtBottomRef.current = true;
      setLiftedFromBottom(false);
      setPendingNewBelow(0);
      onScrolledToBottom?.();
    };
    document.addEventListener('visibilitychange', catchUpIfFollowingTail);
    window.addEventListener('focus', catchUpIfFollowingTail);
    return () => {
      document.removeEventListener('visibilitychange', catchUpIfFollowingTail);
      window.removeEventListener('focus', catchUpIfFollowingTail);
    };
  }, [activeViewportRef, getTailEl, getScrollContentRoot, onScrolledToBottom]);

  /**
   * Рост scrollHeight после первого layout (картинки, шрифты, OverlayScrollbars) без смены tailMessageId
   * — иначе лента остаётся у «верха» при flex-end. Держим низ / кэшированный top, пока пользователь не крутит колесо.
   */
  useLayoutEffect(() => {
    const inner = contentRef.current?.firstElementChild as HTMLElement | null;
    if (!inner) return;

    let rafId = 0;
    const settle = () => {
      const vp = activeViewportRef.current;
      if (!vp) return;
      const innerEl = contentRef.current?.firstElementChild ?? null;
      const tail = innerEl?.lastElementChild ?? null;
      const tailId = tailMessageId;

      clampViewportScrollTop(vp);

      if (initialResumePinActiveRef.current && !userIntentScrollRef.current) {
        applyDmViewportResumePosition(vp, {
          anchor: resumePropToDmAnchor(resumeScrollAnchorRef.current),
          fallbackScrollTop: resumeScrollTopRef.current,
          tailEl: tail,
        });
      } else if (
        (initialBottomPinActiveRef.current && !userIntentScrollRef.current) ||
        (prevAtBottomRef.current && !userIntentScrollRef.current)
      ) {
        const tailJumpBulkLoad = Boolean(contextId && isDirectThreadTailJumpActive(contextId));
        const skipInstantBecauseTailEffectHandlesScroll =
          !tailJumpBulkLoad &&
          prevAtBottomRef.current &&
          !userIntentScrollRef.current &&
          !initialBottomPinActiveRef.current &&
          tailId !== undefined &&
          tailId !== prevTailMessageIdAtResizeSettleRef.current;

        if (!tailJumpBulkLoad && !skipInstantBecauseTailEffectHandlesScroll) {
          scrollMessengerViewportInstant(vp);
        }
        if (
          initialBottomPinActiveRef.current &&
          isMessengerTailStuckToVisualBottom(vp, tail, undefined, innerEl)
        ) {
          initialBottomPinActiveRef.current = false;
        }
      }

      prevTailMessageIdAtResizeSettleRef.current = tailId;
      handleScrollRef.current();
    };

    const ro = new ResizeObserver(() => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        settle();
      });
    });

    ro.observe(inner);
    return () => {
      ro.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [activeViewportRef, contextId, messageListLength, tailMessageId, scrollRevealedForContext]);

  /**
   * Composer растёт от textarea/reply/preview. Если пользователь был у хвоста,
   * изменение высоты нижней панели должно поднять ленту, а не перекрыть последнее сообщение.
   */
  useLayoutEffect(() => {
    const composerEl = composerHostRef.current;
    if (!composerEl) return;

    let prevHeight = composerEl.getBoundingClientRect().height;
    let rafId = 0;
    let pinRafId = 0;

    const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

    const extendComposerPin = (ms: number) => {
      composerPinUntilRef.current = Math.max(composerPinUntilRef.current, nowMs() + ms);
    };

    const isViewportFollowingTailNow = (vp: HTMLElement): boolean => {
      if (composerPinIntentRef.current && nowMs() < composerPinUntilRef.current) return true;
      if (prevAtBottomRef.current) return true;
      return isMessengerViewportAtBottom(vp);
    };

    const beginComposerPinIfFollowing = (vp: HTMLElement | null) => {
      if (!vp) return false;
      const following =
        prevAtBottomRef.current ||
        composerPinIntentRef.current ||
        isMessengerViewportAtBottom(vp);
      if (!following) return false;
      composerPinIntentRef.current = true;
      extendComposerPin(450);
      return true;
    };

    const snapViewportToBottomIfFollowing = () => {
      const vp = activeViewportRef.current;
      if (!vp) return;
      if (!messengerSurfaceVisibleRef.current || !isViewportFollowingTailNow(vp)) return;
      scrollMessengerViewportInstant(vp);
      const atBottom = isMessengerTailStuckToVisualBottom(
        vp,
        getTailEl(),
        undefined,
        getScrollContentRoot()
      );
      const pinActive = composerPinIntentRef.current && nowMs() < composerPinUntilRef.current;
      if (atBottom || pinActive) {
        prevAtBottomRef.current = true;
        setLiftedFromBottom(false);
        if (atBottom) {
          setPendingNewBelow(0);
          onScrolledToBottom?.();
        }
      } else {
        composerPinIntentRef.current = false;
        prevAtBottomRef.current = false;
        setLiftedFromBottom(true);
      }
      onMessagesViewportChange?.();
    };

    const runPinLoop = () => {
      pinRafId = 0;
      snapViewportToBottomIfFollowing();
      if (nowMs() < composerPinUntilRef.current) {
        pinRafId = requestAnimationFrame(runPinLoop);
      } else {
        composerPinIntentRef.current = false;
      }
    };

    const keepTailVisibleIfFollowing = () => {
      rafId = 0;
      const vp = activeViewportRef.current;
      if (!vp || !messengerSurfaceVisibleRef.current) return;
      if (!beginComposerPinIfFollowing(vp)) return;
      if (!pinRafId) {
        pinRafId = requestAnimationFrame(runPinLoop);
      }
    };

    const ro = new ResizeObserver(([entry]) => {
      const nextHeight = entry?.contentRect.height ?? composerEl.getBoundingClientRect().height;
      const delta = nextHeight - prevHeight;
      if (Math.abs(delta) < 0.5) return;
      const vp = activeViewportRef.current;
      const wasFollowing =
        prevAtBottomRef.current ||
        composerPinIntentRef.current ||
        (vp != null && isMessengerViewportAtBottom(vp));
      if (delta > 0 && wasFollowing && vp && messengerSurfaceVisibleRef.current) {
        composerPinIntentRef.current = true;
        extendComposerPin(450);
        scrollMessengerViewportInstant(vp);
      }
      prevHeight = nextHeight;
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(keepTailVisibleIfFollowing);
    });

    const onComposerBeforeInput = () => keepTailVisibleIfFollowing();
    const onComposerInput = () => keepTailVisibleIfFollowing();

    ro.observe(composerEl);
    composerEl.addEventListener('beforeinput', onComposerBeforeInput);
    composerEl.addEventListener('input', onComposerInput);
    return () => {
      ro.disconnect();
      composerEl.removeEventListener('beforeinput', onComposerBeforeInput);
      composerEl.removeEventListener('input', onComposerInput);
      if (rafId) cancelAnimationFrame(rafId);
      if (pinRafId) cancelAnimationFrame(pinRafId);
      composerPinIntentRef.current = false;
      composerPinUntilRef.current = 0;
    };
  }, [activeViewportRef, getTailEl, getScrollContentRoot, onMessagesViewportChange, onScrolledToBottom]);

  const handleUserScrollIntent = useCallback(() => {
    userIntentScrollRef.current = true;
    initialResumePinActiveRef.current = false;
    initialBottomPinActiveRef.current = false;
  }, []);

  useLayoutEffect(() => {
    if (externalScrollIntentSignal === undefined) return;
    userIntentScrollRef.current = true;
    initialResumePinActiveRef.current = false;
    initialBottomPinActiveRef.current = false;
    prevAtBottomRef.current = false;
    setLiftedFromBottom(true);
  }, [externalScrollIntentSignal]);

  // ── Jump to latest (scroll-down FAB click) ─────────────────────

  const jumpToLatest = useCallback(async () => {
    cancelMessengerTailScrollAnimation();
    try {
      await onBeforeJumpToLatest?.();
    } catch {
      return;
    }
    cancelMessengerTailScrollAnimation();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tail = getTailEl();
        scrollMessengerTailIntoView(activeViewportRef.current, tail, 'smooth');
        prevAtBottomRef.current = true;
        setLiftedFromBottom(false);
        setPendingNewBelow(0);
        onMessagesViewportChange?.();
      });
    });
  }, [activeViewportRef, getTailEl, onBeforeJumpToLatest, onMessagesViewportChange]);

  // ── DnD ────────────────────────────────────────────────────────

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (acceptDrop) setIsDraggingOver(true);
      onDragOver?.(e);
    },
    [acceptDrop, onDragOver]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!dropzoneRef.current?.contains(e.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleInternalDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      onDrop?.(e);
    },
    [onDrop]
  );

  // ── Sync external viewportRef ──────────────────────────────────
  // OverlayScrollArea writes to activeViewportRef directly when externalViewportRef
  // is provided, so no extra sync is needed.

  const messagesRevealed =
    (contextId === undefined || scrollRevealedForContext === contextId) && !messagesRevealBlocked;
  const messagesWrapClassName = [
    'messengerChat__messagesWrap',
    !messagesRevealed && !messagesRevealBlocked
      ? 'messengerChat__messagesWrap--initialScrollPending'
      : '',
    messagesRevealBlocked ? 'messengerChat__messagesWrap--revealBlocked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="messengerChat">
      {header}

      <div
        ref={dropzoneRef}
        className={messagesWrapClassName}
        onDragOver={acceptDrop ? handleDragOver : undefined}
        onDragLeave={acceptDrop ? handleDragLeave : undefined}
        onDrop={acceptDrop ? handleInternalDrop : undefined}
        onWheel={handleUserScrollIntent}
        onTouchMove={handleUserScrollIntent}
      >
        <OverlayScrollArea
          className="messengerChat__messages"
          viewportRef={activeViewportRef}
          contentRef={contentRef}
          onScroll={handleScroll}
          defer={false}
        >
          <div
            className={[
              'messengerChat__messagesInner',
              messagesRevealed ? 'messengerChat__messagesInner--enterReady' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <ChatViewMessageFeedSlot>{children}</ChatViewMessageFeedSlot>
          </div>
        </OverlayScrollArea>

        {messagesRevealBlocked ? (
          <MessengerMessagesLoader
            className="messengerChat__messagesRevealLoader"
            label="Переход к найденному сообщению"
          />
        ) : null}

        {isDraggingOver && acceptDrop ? <DropZoneOverlay /> : null}

        {!messagesRevealBlocked ? (
          <MessengerScrollDownFab
            lifted={liftedFromBottom}
            pendingCount={
              scrollDownBadgeOverride !== undefined ? scrollDownBadgeOverride : pendingNewBelow
            }
            streamCue={streamCue}
            onClick={jumpToLatest}
          />
        ) : null}
      </div>

      <div ref={composerHostRef} className="messengerChat__composerHost">
        {composer}
      </div>
    </div>
  );
}
